import React from "react";
import { Input, Select, DatePicker, InputNumber, Row, Col } from "antd";
import { SectionCard } from "../../../../../components/shared";
import dayjs from "dayjs";
import { LabOrder, parseTestType } from "../../../../../types/lab/LabOrderTypes";
import { TestCategory, TestType } from "../../../../../types/healthcare/TestTypes";
import { LabOrderPriorityOptions, LabOrderStatusOptions } from "../../../../../types/lab/LabOrderTypes";
import {sampleTypeOptions} from "../../../../../types/lab/SampleTypes";

const { TextArea } = Input;

interface OrderDetailsProps {
	orderData: Partial<LabOrder>;
	updateOrderData: (field: string, value: any) => void;
	setErrorMessages: React.Dispatch<React.SetStateAction<any[]>>;
	testCategories: TestCategory[];
	testTypes: TestType[];
	onCategoryChange: (categoryValue: string) => void;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ orderData, updateOrderData, setErrorMessages, testCategories, testTypes, onCategoryChange }) => {
	const isDisabled = orderData.status === "completed";

	const testCategoryOptions = testCategories
		.filter((category) => category.name === "Pathology" || category.name === "Genetic Testing" || category.name === "Laboratory" || category.name === "Blood Tests")
		.map((category) => ({
			label: category.name,
			value: category.value,
		}));

	const testTypeOptions = testTypes.map((testType) => ({
		label: testType.name,
		value: testType.value,
	}));

	const handleCategoryChange = (categoryValue: string) => {
		updateOrderData("test_category", categoryValue);
		updateOrderData("test_type", []);
		onCategoryChange(categoryValue);
	};

	// Parse test_type to array for display
	const currentTestTypes = parseTestType(orderData.test_type);

	return (
		<SectionCard title="Order Information" style={{ marginBottom: 16 }}>
			<Row gutter={[12, 12]}>
				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>
						Order Number <span style={{ color: "var(--mg-danger)" }}>*</span>
					</label>
					<Input value={orderData.order_number || ""} onChange={(e) => updateOrderData("order_number", e.target.value)} disabled={isDisabled} placeholder="Enter order number" size="small" />
				</Col>

				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>
						Test Category <span style={{ color: "var(--mg-danger)" }}>*</span>
					</label>
					<Select
						value={orderData.test_category || null}
						options={testCategoryOptions}
						onChange={(value) => handleCategoryChange(value)}
						disabled={isDisabled}
						placeholder="Select Test Category"
						size="small"
						showSearch
						filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
						style={{ width: "100%" }}
					/>
				</Col>

				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>
						Test Types <span style={{ color: "var(--mg-danger)" }}>*</span>
					</label>
					<Select
						mode="multiple"
						value={currentTestTypes}
						options={testTypeOptions}
						onChange={(value) => {
							console.log("OrderDetails - Selected test types:", value); // Debug log
							updateOrderData("test_type", value);
						}}
						disabled={isDisabled || !orderData.test_category}
						placeholder="Select Test Types"
						size="small"
						showSearch
						filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
						notFoundContent="Select a category first"
						style={{ width: "100%" }}
						maxTagCount="responsive"
					/>
				</Col>

				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>
						Sample Type <span style={{ color: "var(--mg-danger)" }}>*</span>
					</label>
					<Select
						value={orderData.sample_type || null}
						options={sampleTypeOptions}
						onChange={(value) => updateOrderData("sample_type", value)}
						disabled={isDisabled}
						placeholder="Select Sample Type"
						size="small"
						style={{ width: "100%" }}
					/>
				</Col>

				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>
						Status <span style={{ color: "var(--mg-danger)" }}>*</span>
					</label>
					<Select value={orderData.status || null} options={LabOrderStatusOptions} onChange={(value) => updateOrderData("status", value)} disabled={isDisabled} placeholder="Select Status" size="small" style={{ width: "100%" }} />
				</Col>

				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>
						Priority <span style={{ color: "var(--mg-danger)" }}>*</span>
					</label>
					<Select value={orderData.priority || null} options={LabOrderPriorityOptions} onChange={(value) => updateOrderData("priority", value)} disabled={isDisabled} placeholder="Select Priority" size="small" style={{ width: "100%" }} />
				</Col>

				<Col xs={24} sm={12} md={8}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Ordered At</label>
					<DatePicker
						value={orderData.ordered_at ? dayjs(orderData.ordered_at) : null}
						onChange={(date) => updateOrderData("ordered_at", date?.toISOString())}
						showTime={{ format: "HH:mm" }}
						format="DD/MM/YYYY HH:mm"
						disabled={isDisabled}
						placeholder="Select ordered date and time"
						size="small"
						style={{ width: "100%" }}
					/>
				</Col>

				<Col xs={24}>
					<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Notes</label>
					<TextArea value={orderData.notes || ""} onChange={(e) => updateOrderData("notes", e.target.value)} disabled={isDisabled} placeholder="Enter any additional notes" rows={3} size="small" />
				</Col>
			</Row>
		</SectionCard>
	);
};

export default OrderDetails;
